import {
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  SparklesIcon,
} from "@heroicons/react/20/solid";
import { useEffect, useState } from "react";
import { getSerpAssitance } from "../../../apiFunctions/sop/content";
import { connect, useDispatch, useSelector } from "react-redux";
import {
  NotificationStatus,
  setNotification,
} from "../../../store/main/actions";
import PremiumModal from "../PremiumModal";
import { useRouter } from "next/router";
import { parseError } from "../../../utils/common";

interface CountedInputProps {
  id: string;
  label: string;
  onChange: (value: any) => void;
  placeholder?: string;
  maxLength?: number;
  minLength?: number;
  required?: boolean;
  inputClass?: string;
  type?: string;
  error?: string;
  name?: string;
  defaultValue: string;
  helpText?: string;
  description?: string;
  disabled: boolean;
  className?: string;
  disableLength?: boolean;
  minValue?: number;
  maxValue?: number;
  setErrorSubmission?: (value: boolean) => void;
  assistance?: boolean;
  inputDependencies?: Array<string>;
  helpType?: string;
  inputs: any;
  handleParentChange?: (e: any, key: string) => void;
}
const CountedInput: React.FC<CountedInputProps> = ({
  id,
  label,
  onChange,
  placeholder,
  maxLength = 100,
  minLength = 3,
  required,
  inputClass,
  type,
  error,
  name,
  defaultValue,
  helpText,
  description,
  disabled = false,
  setErrorSubmission = () => {},
  className,
  disableLength = true,
  minValue,
  maxValue,
  assistance,
  inputDependencies,
  helpType,
  inputs,
  handleParentChange,
}) => {
  const [input, setInput] = useState<string>(defaultValue ?? "");
  const [err, setErr] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [premiumModalOpen, setPremiumModalOpen] = useState<boolean>(false);
  const [helpDone, setHelpDone] = useState<boolean>(false);
  const dispatch = useDispatch();

  const router = useRouter();
  const sopTitle = useSelector((state: any) => state.sop.sopTitle);
  const query = router.query;
  const { sopId } = query;

  useEffect(() => {
    setInput(defaultValue);
  }, [defaultValue]);

  const handleErr = () => {
    if (err.length > 0) {
      setErrorSubmission(true);
    } else setErrorSubmission(false);
  };
  const warningError = "You are close exceeding the word limit";
  const handleChange = (e: any) => {
    setInput(e.target.value);

    if (e.target.value.length >= maxLength) {
      setErr(`Maximum ${maxLength} characters allowed`);
    } else if (e.target.value.length > maxLength) {
      setErr(warningError);
    } else {
      setErr("");
    }

    handleErr();

    onChange(e);
  };

  const getAssitance = async () => {
    let flag = false;
    try {
      setHelpDone(false);
      setLoading(true);
      if (!inputDependencies) return;

      let inp: any = {};
      inputDependencies.map((key: string) => {
        if (inputs && inputs[key]) {
          inp[key] = inputs[key];
        } else {
          dispatch(
            setNotification({
              status: NotificationStatus.failed,
              message: `Please enter field for ${key}`,
            })
          );
          setLoading(false);
          flag = true;
          return;
        }
      });

      Object.keys(inp).map((key) => {
        if (!inp[key]) {
          dispatch(
            setNotification({
              status: NotificationStatus.failed,
              message: `Please enter input for ${key}`,
            })
          );
          setLoading(false);
          flag = true;
          return;
        }
      });
      if (flag) {
        return;
      }
      let data: any = await getSerpAssitance(
        inp,
        helpType ?? "",
        sopId as string,
        query?.type as string
      ).catch((err) => {
        if (err.response?.status === 402) {
          setLoading(false);
          setPremiumModalOpen(true);
          return;
        } else {
          dispatch(
            setNotification({
              status: NotificationStatus.failed,
              message: parseError(err) ?? "Something went wrong",
            })
          );
        }
      });
      if (!data) return;

      if (typeof data?.output === "object") {
        Object.keys(data?.output).map((key) => {
          if (handleParentChange) {
            handleParentChange({ target: { value: data?.output[key] } }, key);
          }
        });
      } else {
        handleChange({ target: { value: data?.output } });
      }
    } catch (err: any) {
      if (err.response?.status === 402) {
        setLoading(false);
        setPremiumModalOpen(true);
        return;
      }
      dispatch(
        setNotification({
          status: NotificationStatus.failed,
          message: "Something went wrong",
        })
      );
    } finally {
      setLoading(false);
      setHelpDone(true);
    }
  };
  return (
    <>
      <PremiumModal open={premiumModalOpen} setOpen={setPremiumModalOpen} />
      <div className={!className ? "my-6" : className}>
        <div className="flex justify-initial">
          <label
            htmlFor={id}
            className="flex justify-between items-center w-full text-sm mb-1 text-tootler-grey-600"
          >
            <span>
              {label}{" "}
              <span className="text-red-400"> {required ? "*" : ""} </span>
            </span>

            {assistance && (
              <button
                className={`text-tootler-primary-900 font-bold text-sm focus:border-2 flex items-center gap-2 rounded-lg p-1 focus:border-gray-400 ml-auto ${
                  loading && "animate-bounce"
                } `}
                onClick={getAssitance}
              >
                <SparklesIcon className="h-3.5 w-3.5" />
                AutoFill
              </button>
            )}
          </label>
        </div>

        <div className="relative rounded-sm">
          {/* <GrammarlyEditorPlugin clientId={process.env.NEXT_PUBLIC_GRAMMARLY_TOKEN}> */}
          <input
            maxLength={maxLength}
            minLength={minLength}
            value={input}
            onChange={handleChange}
            type={type}
            name={name}
            id={id}
            className={`rounded-xl text-base w-full font-medium h-12 p-4 border border-tootler-grey-200 outline-tootler-primary-900 placeholder-tootler-grey-300 text-tootler-grey-900  bg-tootler-grey-100 hover:bg-tootler-grey-200/80 transition
            ${loading ? "opacity-40" : "opacity-100"}
            ${helpDone ? "border-tootler-primary-400" : "border-slate-400"}`}
            placeholder={placeholder}
            defaultValue={defaultValue}
            aria-describedby={id}
            required={required}
            disabled={disabled || loading}
            min={minValue}
            max={maxValue}
            step="any"
          />
          {loading && (
            <div className="absolute bottom-2.5 right-0" role="status">
              <svg
                aria-hidden="true"
                className="inline w-6 h-6 mr-2 text-gray-200 animate-spin fill-primary-500"
                viewBox="0 0 100 101"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                  fill="currentColor"
                />
                <path
                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                  fill="currentFill"
                />
              </svg>
              <span className="sr-only">Loading...</span>
            </div>
          )}
          {/* </GrammarlyEditorPlugin> */}
          {/* <GrammarlyButton/> */}

          {disableLength ? null : (
            <div className="pointer-events-none absolute text-gray-400 inset-y-0 right-0 top-14 flex items-center pr-2 text-xs">
              {input?.length}/{maxLength}
            </div>
          )}
          {error && (
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
              <ExclamationCircleIcon
                className="h-5 w-5 text-red-500"
                aria-hidden="true"
              />
            </div>
          )}
        </div>
        <p className="mt-2 text-xs text-gray-600" id={id}>
          {helpText}
        </p>
        {err && (
          <div className="flex space-x-2 items-center">
            <ExclamationCircleIcon
              className={`h-5 w-5 text-semantic-error  ${
                err == warningError ? "hidden" : "flex"
              }`}
            />
            <ExclamationTriangleIcon
              className={`h-5 w-5 text-semantic-warning  ${
                err == warningError ? "flex" : "hidden"
              }`}
            />
            <p
              className={` text-sm ${
                err == warningError
                  ? "text-semantic-warning"
                  : "text-semantic-error"
              }`}
              id={id}
            >
              {err}
            </p>
          </div>
        )}
      </div>
    </>
  );
};

const mapStateToProps = (state: any) => {
  return {
    inputs: state.sop?.inputs,
  };
};
export default connect(mapStateToProps)(CountedInput);
